import axios from 'axios';
import { textShapes, useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useActionStore } from '@/stores/v2/action';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { calculatePrintAreaV2 } from './index';
import { determineRangeSAPCode } from '@/utils/generatePriceParamsEventPayload';
import { px2mm } from '@/utils';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  NewProofForm,
  NewProductPrintPositionForms,
  CategoryConfiguration,
  LanyardConfiguration,
  RegularConfiguration,
  PmsColor,
  ProductPrintPosition,
  PrintTechnique,
  Category,
  CreatePersonalizationForm,
  AnchorPoint,
} from '@midocean/webapps-shared-domain';
import { PipelineNotificationType } from '@/components/Notifiers/typings/PipelineNotificationType';
import customerService from '@/services/CustomersService';
import { UpdatePersonalizationCommand } from '@/web-components/import-wizard/typings/common/createPersonalization';
import { ProductColorSetterType } from '@/setters/typings/common/ProductColorsSetterType';
import {
  AnnotationShape,
  LogoShape,
  Text,
  TextShape,
} from '@/web-components/import-wizard-v2/typings/common';
import { UpdateCustomerArtwork } from '@/web-components/logo-library/typings';
import { format } from 'date-fns';
import { ProofPreview } from '@/libs/proof-approval/typings/common/Personalization';
import { PdpChoicesSetterType } from '@/setters/typings';
import { NotificationResult } from '@/web-components/import-wizard-v2/typings/notification';
import { getUtcDateTime } from './common';
import { RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE } from '@/constants/featureFlags';

export function getArtworkFiles(shapes: AnnotationShape[]): File[] {
  const logoShapes: LogoShape[] = shapes.filter(
    (x) => x.kind === 'Logo'
  ) as LogoShape[];

  let files: File[] = [];

  logoShapes.map((artwork) => {
    if (artwork.traditionalTechniquePngFile) {
      const traditionalColorBlob = new File(
        [artwork.traditionalTechniquePngFile],
        artwork.personalizationArtworkId + '-traditional-tech-png',
        {
          type: 'image/png',
        }
      );

      const fullColorBlob = new File(
        [artwork.fullColorTechniquePngFile],
        artwork.personalizationArtworkId,
        {
          type: 'image/png',
        }
      );

      files = [...files, traditionalColorBlob, fullColorBlob];
    } else {
      // keep same flow as is, in case that file converter is not enebled and used
      const imageBlob =
        artwork.variantArtworkImages && artwork.variantArtworkImages.length > 0
          ? artwork.pngFileWithoutConverters
          : artwork.pngFile;

      const pngFile = new File([imageBlob], artwork.personalizationArtworkId, {
        type: 'image/png',
      });
      files = [...files, pngFile];
    }
  });

  return files;
}

export async function markCustomerArtworksAsUsed(
  shapes: AnnotationShape[]
): Promise<void> {
  shapes.forEach(async (shape) => {
    if (shape.kind == 'Logo' && shape.customerArtworkId !== '') {
      const updateArtwork: UpdateCustomerArtwork = {
        newTitle: undefined,
        newTagsList: undefined,
        markAsUsed: true,
      };

      await customerService.update(shape.customerArtworkId, updateArtwork);
    }
  });
}

export function getSmallDetailsFiles(shapes: AnnotationShape[]): File[] {
  const files: File[] = [];
  const logoShapes: LogoShape[] = shapes.filter(
    (x) => x.kind === 'Logo'
  ) as LogoShape[];

  const textShapes: TextShape[] = shapes.filter(
    (x) => x.kind === 'Text'
  ) as TextShape[];

  logoShapes.map((a) => {
    if (a.smallDetailsResult) {
      files.push(
        new File(
          [a.smallDetailsResult.pngFileBlob],
          a.personalizationArtworkId,
          {
            type: 'image/png',
          }
        )
      );
    }
  });

  textShapes.map((t) => {
    if (t.smallDetailsResult) {
      files.push(
        new File([t.smallDetailsResult.pngFileBlob], t.id, {
          type: 'image/png',
        })
      );
    }
  });

  return files;
}

export function getTextFiles(shapes: AnnotationShape[]): File[] {
  const files: File[] = [];
  const textShapes: TextShape[] = shapes.filter(
    (x) => x.kind === 'Text'
  ) as TextShape[];

  textShapes.map((t) => {
    files.push(
      new File([t.pngFileWithoutConverters], t.id, {
        type: 'image/png',
      })
    );
  });

  return files;
}

export function getVariantFiles(shapes: AnnotationShape[]): File[] {
  const variantFiles: File[] = [];

  shapes.forEach((artwork) => {
    if (artwork.variantArtworkImages) {
      artwork.variantArtworkImages.forEach((previewImage) => {
        variantFiles.push(
          new File(
            [previewImage.variantArtworkPng],
            artwork.id + '@' + previewImage.variantColor,
            {
              type: 'image/png',
            }
          )
        );
      });
    }
  });
  return variantFiles;
}

export const getPositionImageDPI = (
  positionId: string,
  productPrintPositions: ProductPrintPosition[]
): number => {
  return productPrintPositions?.find((p) => p.id === positionId)
    ?.originalImageWebDpi as number;
};

export const getPrintTechniqueSapCode = (
  pId: string,
  tId: string,
  productPrintPositions: ProductPrintPosition[]
): string => {
  return productPrintPositions
    .filter((p) => p.id === pId)
    .map(({ printTechniques }) =>
      printTechniques?.find((pt) => pt.id === tId)
    )[0]?.printTechnique?.code as string;
};

export const getPrintTechnique = (
  pId: string,
  tId: string,
  productPrintPositions: ProductPrintPosition[]
): PrintTechnique | undefined => {
  return productPrintPositions
    .filter((p) => p.id === pId)
    .map(({ printTechniques }) =>
      printTechniques?.find((t) => t.id === tId)
    )[0];
};

export const getConvertedColorToPms = (
  colorName: string,
  availablePmsColors: PmsColor[]
): PmsColor | undefined => {
  return availablePmsColors?.find((color) => {
    return color.name === colorName;
  });
};

export const logosByPosition = (
  positionId: string,
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[]
): LogoShape[] =>
  shapes.filter((shape) =>
    positionShapeMap.some(
      (psm) =>
        psm.id === positionId &&
        psm.logoId === shape.id &&
        shape.kind === 'Logo'
    )
  ) as LogoShape[];

export const textsByPosition = (
  positionId: string,
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[]
): TextShape[] =>
  shapes
    .filter((shape) =>
      positionShapeMap.some(
        (psm) => psm.id === positionId && psm.logoId === shape.id
      )
    )
    .filter(textShapes) as TextShape[];

export const convertedColorToPms = (text: Text): PmsColor => {
  return {
    name: text.colorName,
    originalName: text.colorName,
    red: text.color[0],
    green: text.color[1],
    blue: text.color[2],
    colorFuzz: 4,
    isOriginalSpotColor: false,
  } as PmsColor;
};

export const extractCommercialRemarks = (
  notifications: NotificationResult[]
): string[] => {
  return notifications
    .filter(
      (notification: NotificationResult) =>
        notification.validatorName === 'commercialRemark'
    )
    .map((notification) => notification.message);
};

export const mapProductCategoryConfiguration = (
  productPrintPosition: ProductPrintPosition
): CategoryConfiguration => {
  const mirrorStore = useMirrorStore();

  switch (productPrintPosition.category) {
    case Category.Lanyard: {
      const mirrorSettings = mirrorStore.positionMirrorSettings.find(
        (x) => x.positionId === productPrintPosition.id
      );
      return {
        $type:
          'Customers.Domain.Models.LanayrdPrintPositionCategoryConfiguration, Customers.Domain',
        isMirrorApplied: mirrorSettings?.isMirrorApplied,
        isOppositeRotationApplied: mirrorSettings?.isOppositeRotationApplied,
      } as LanyardConfiguration;
    }
    default: {
      return {
        $type:
          'Customers.Domain.Models.RegularPrintPositionCategoryConfiguration, Customers.Domain',
      } as RegularConfiguration;
    }
  }
};

export function getProofFormFromState(isPdfPreview: boolean): NewProofForm {
  const productStore = useProductStore();
  const shapeStore = useShapeStore();

  for (let i = 0; i < shapeStore.shapes.length; i++) {
    shapeStore.shapes[i].zindex = i;
  }

  const actionStore = useActionStore();

  const newProofForm: NewProofForm = {
    newProductPrintPositionForms: productStore.unavailablePrintPositions.map(
      (u) => {
        const productPrintPositions = productStore.printPositions;
        const positions = shapeStore.positionShapeMap.filter(
          (position: Record<string, string>) => {
            return position.id === u.positionId;
          }
        );

        const { width, height } = calculatePrintAreaV2(
          positions,
          shapeStore.shapes
        );

        const printTechnique = getPrintTechnique(
          u.positionId,
          u.technique.id,
          productPrintPositions
        ) as PrintTechnique;

        const productPrintPosition = productStore.printPositions.find(
          (x) => x.id === u.positionId
        );

        return {
          name: u.positionName,
          SAPCode: u.positionName,
          printSizeWidthMm: width,
          printSizeHeightMm: height,
          category: productPrintPosition?.category ?? Category.Regular,
          categoryConfiguration: productPrintPosition
            ? mapProductCategoryConfiguration(productPrintPosition)
            : null,
          positionKind:
            productPrintPosition?.anchorPoint &&
            RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE
              ? 'Standard'
              : 'Custom',
          productPrintTechnique: {
            name: u.technique.techniqueName,
            SAPCode: getPrintTechniqueSapCode(
              u.positionId,
              u.technique.id,
              productPrintPositions
            ),
            SAPRangeCode: determineRangeSAPCode(width, height, printTechnique),
          },
          personalizationTexts: textsByPosition(
            u.positionId,
            shapeStore.shapes,
            shapeStore.positionShapeMap
          ).map((text) => {
            const angle = ((text.rotation as number) * 180) / Math.PI;

            return {
              id: text.id,
              zIndex: text.zindex,
              distanceFromTop: text?.yOnProductMM,
              distanceFromLeft: text?.xOnProductMM,
              width: text.widthOnProductMM,
              height: text.heightOnProductMM,
              rotationAngle: angle.toFixed(),
              font: text.w2pText.fontFamily,
              fontSize: text.w2pText.fontSizePT,
              bold: text.w2pText.fontWeight === 'bold',
              italic: text.w2pText.fontStyle === 'italic',
              underline: false,
              text: text.w2pText.text,
              textHtml: text.w2pText.textHtml,
              textAlignement: text.w2pText.textAlign,
              pmsColorInfo: convertedColorToPms(text.w2pText),
              originalWidthMM: text.originalWidthMM,
              originalHeightMM: text.originalHeightMM,
              variantArtworkImages: text.variantArtworkImages,
              isOnRecommendedPrintPosition: text.isOnRecommendedPrintPosition,
              pdfUrl: text.pdfUrl,
              distanceFromAnchorPoint: getDistanceFromAnchorPoint(
                productPrintPosition?.anchorPoint,
                text?.yOnProductMM
              ),
              smallDetails:
                DoSmallDetailsPart &&
                text &&
                text.smallDetailsResult &&
                text.smallDetailsResult.status !== 'NoCheck'
                  ? {
                      positiveLoss: text.smallDetailsResult.positiveDetailsLoss,
                      negativeLoss: text.smallDetailsResult.negativeDetailsLoss,
                      status: text.smallDetailsResult.status,
                      calculatedQuality:
                        text.smallDetailsResult.calculatedQuality,
                      approvedAt:
                        !isPdfPreview && text.smallDetailsResult.approvedAt
                          ? getUtcDateTime(text.smallDetailsResult.approvedAt)
                          : null,
                    }
                  : null,
            };
          }),
          personalizationArtworks: logosByPosition(
            u.positionId,
            shapeStore.shapes,
            shapeStore.positionShapeMap
          ).map((artwork) => {
            const shape = shapeStore.shapes?.find((x) => x.id === artwork.id);
            const angle = ((shape?.rotation as number) * 180) / Math.PI;
            return {
              id: artwork.personalizationArtworkId,
              zIndex: artwork.zindex,
              isRoundLogo: artwork.isRoundLogo,
              widthWithCropping: shape?.widthOnProductMM,
              heightWithCropping: shape?.heightOnProductMM,
              pmsColorsInPngFile: artwork.pmsColorsInPngFile,
              originalWidthMM: artwork.originalWidthMM,
              originalHeightMM: artwork.originalHeightMM,
              originalVerticalResolutionDPIs:
                artwork.originalVerticalResolutionDPIs,
              originalHorizontalResolutionDPIs:
                artwork.originalHorizontalResolutionDPIs,
              pngHorizontalResolutionDPIs:
                artwork.pngHorizontalResolutionDPIs.toFixed(), // NOTE: Recalculate DPIs using formula
              pngVerticalResolutionDPIs:
                artwork.pngVerticalResolutionDPIs.toFixed(), // NOTE: Recalculate DPIs using formula
              pngFileUrl: artwork.pngFileUrl,
              pngWidthPixels: artwork.pngWidthPixels,
              pngHeightPixels: artwork.pngHeightPixels,
              distanceFromTopWithCropping: shape?.yOnProductMM,
              distanceFromLeftWithCropping: shape?.xOnProductMM,
              rotationAngle: angle.toFixed(),
              title: artwork.title,
              fileName: artwork.fileName,
              isVectorFileTrueVectorFile: artwork.isVectorFileTrueVectorFile,
              hasGradient: artwork.hasGradient,
              hasTransparency: artwork.hasTransparency,
              isFileConvertedToCmykPdf: artwork.isFileConvertedToCmykPdf,
              areCmykValuesCorrect: artwork.areCmykValuesCorrect,
              originalFileUrl: artwork.originalFileUrl,
              isOnRecommendedPrintPosition:
                artwork.isOnRecommendedPrintPosition,
              distanceFromAnchorPoint: getDistanceFromAnchorPoint(
                productPrintPosition?.anchorPoint,
                shape?.yOnProductMM as number
              ),
              cmykPdfFileUrl: artwork.cmykPdfFileUrl
                ? artwork.cmykPdfFileUrl
                : '',
              userActions: actionStore.prepareActionsPayload(
                artwork.personalizationArtworkId
              ),
              smallDetails:
                DoSmallDetailsPart &&
                shape &&
                shape.smallDetailsResult &&
                shape.smallDetailsResult.status !== 'NoCheck'
                  ? {
                      positiveLoss:
                        shape.smallDetailsResult.positiveDetailsLoss,
                      negativeLoss:
                        shape.smallDetailsResult.negativeDetailsLoss,
                      status: shape.smallDetailsResult.status,
                      calculatedQuality:
                        shape.smallDetailsResult.calculatedQuality,
                      approvedAt:
                        !isPdfPreview && shape.smallDetailsResult.approvedAt
                          ? getUtcDateTime(shape.smallDetailsResult.approvedAt)
                          : null,
                    }
                  : null,
            };
          }),
        } as unknown as NewProductPrintPositionForms;
      }
    ),
  };

  return newProofForm;
}

export function isShapeColorChanged(): boolean | undefined {
  const shapeStore = useShapeStore();
  const productStore = useProductStore();
  const colorsManagementStore = useColorsManagementStore();
  const posShapes = shapeStore.positionShapeMap.filter(
    (element) => element.id === productStore.activePosition.id
  );
  let shapeColorElement: PmsColor | undefined;
  //let isAvailablePmsColor = true;

  shapeStore.shapes.forEach((shape: AnnotationShape) => {
    if (
      shape.kind === 'Logo' &&
      posShapes.find((element) => element.logoId === shape.id)
    ) {
      shapeColorElement = shape.pmsColorsInPngFile.find(
        (color: PmsColor) =>
          color.name !== color.originalName && color.originalName !== undefined
      );
    }
  });

  // if (shapeColorElement) {
  //   isAvailablePmsColor =
  //     Object.keys(
  //       colorsManagementStore.availablePmsColors?.find((c) => {
  //         return c.name === shapeColorElement?.originalName;
  //       }) || ({} as PmsColor)
  //     ).length !== 0;
  // }

  return shapeColorElement !== undefined; //&& !isAvailablePmsColor;
}

export const trySavePersonalization = async (
  isEditMode: boolean,
  pdpChoicesProductVariants: Array<ProductColorSetterType>,
  sapCustomerId: string,
  isConcept: boolean,
  isDirectApproval: boolean,
  customerCompanyLogoUrl: string,
  languageInUse: string,
  isCommunicationSentToCC: boolean
): Promise<string> => {
  const shapeStore = useShapeStore();
  const productStore = useProductStore();
  const notificationStore = useNotificationStore();

  const { shapes, personalization } = shapeStore;
  const { sku, name } = productStore.data;
  await markCustomerArtworksAsUsed(shapes);
  const files = await getArtworkFiles(shapes);
  const variantFiles = getVariantFiles(shapes);
  const smallDetailsFiles = DoSmallDetailsPart
    ? getSmallDetailsFiles(shapes)
    : [];
  const textFiles = getTextFiles(shapes);

  if (!isEditMode) {
    const form: CreatePersonalizationForm = {
      externalCustomerId: sapCustomerId,
      productSKU: sku,
      productName: name,
      isConcept: isConcept,
      isDirectApproval: isDirectApproval,
      createdFromMocca: shapeStore.isMoccaPersonalization,
      customerCompanyLogoUrl: customerCompanyLogoUrl,
      contactLanguageCode: languageInUse,
      sendMailToCustomerCare: isCommunicationSentToCC,
      creator: '',
      proofType: 'AdvancedPersonalization',
      productVariants: pdpChoicesProductVariants,
      newProofForm: getProofFormFromState(false),
      remarkCodes: extractCommercialRemarks(notificationStore.notifications),
    } as unknown as CreatePersonalizationForm;

    const {
      data: { id: personalizationId },
    } = await customerService.submitPersonalization(
      files,
      variantFiles,
      smallDetailsFiles,
      textFiles,
      form
    );

    return personalizationId;
  } else {
    const command: UpdatePersonalizationCommand = {
      name: personalization.proofs[0].name ?? '',
      remarkCodes: extractCommercialRemarks(notificationStore.notifications),
      ...getProofFormFromState(false),
    };
    await customerService.updatePersonalization(
      files,
      variantFiles,
      smallDetailsFiles,
      textFiles,
      command,
      personalization.id,
      personalization.proofs[0].id
    );

    //Check if Order this item btn is clicked, if yes - complete personalization
    if (!isConcept) {
      await customerService.completePersonalization(personalization.id);
    }

    return personalization.id;
  }
};

export const generateProofPreview = async (
  smallDetailsFiles: File[],
  sapCustomerId: string,
  customerCompanyLogoUrl: string,
  languageInUse: string,
  pdpChoices: PdpChoicesSetterType
): Promise<ProofPreview> => {
  const shapesStore = useShapeStore();
  const productStore = useProductStore();
  const notificationStore = useNotificationStore();

  const files = getArtworkFiles(shapesStore.shapes);
  const variantFiles = getVariantFiles(shapesStore.shapes);
  const textFiles = getTextFiles(shapesStore.shapes);
  const { productVariants: pdpChoicesProductVariants } = pdpChoices;
  const { sku, name } = productStore.data;
  const { newProductPrintPositionForms } = getProofFormFromState(true);
  const remarkCodes = extractCommercialRemarks(notificationStore.notifications);

  const { data } = await customerService.generateProofPreview(
    files,
    variantFiles,
    smallDetailsFiles,
    textFiles,
    {
      externalCustomerId: sapCustomerId,
      productSKU: sku,
      productName: name,
      customerCompanyLogoUrl: customerCompanyLogoUrl,
      contactLanguageCode: languageInUse,
      proofType: 'AdvancedPersonalization',
      productVariants: pdpChoicesProductVariants,
      productPrintPositionForms: newProductPrintPositionForms,
      remarkCodes: remarkCodes,
    }
  );

  return data;
};

export function triggerSmallDetailsCheck(): boolean {
  const shapeStore = useShapeStore();

  return (
    shapeStore.shapes.filter(
      (x) =>
        x.smallDetailsResult &&
        (x.smallDetailsResult.status === 'Average' ||
          x.smallDetailsResult.status === 'Bad' ||
          !x.smallDetailsResult.isCheckValid)
    ).length > 0
  );
}

export function isRevalidationNeeded(): boolean {
  const shapeStore = useShapeStore();

  return (
    shapeStore.shapes.filter(
      (x) => x.smallDetailsResult && x.smallDetailsResult.isCheckValid === false
    ).length > 0
  );
}
function getDistanceFromAnchorPoint(
  anchorPoint: AnchorPoint | undefined,
  yOnProductMM: number
): number {
  // calculate distance from anchor point till the top of the shape
  if (anchorPoint) {
    return Math.abs(anchorPoint.distanceFromTop - yOnProductMM);
  }
  return 0;
}
